import { useQuery } from '@tanstack/react-query';
import { useAPI } from "/src/hooks/useAPI";

/**
 * Fetch minimal training programs (faster, less data)
 */
const fetchMinimalTrainingPrograms = async (apiBaseUrl, authToken) => {
  const {callAPI} = useAPI();

  if (!authToken || !apiBaseUrl) {
    throw new Error('API base URL and auth token are required');
  }

  try{
    const data = await callAPI(
      `trainingprograms?show_subscribed=true&minimal=true`,
      {
        token: authToken ,
      }
    );
    return data
  }
  catch(error){
    throw new Error('Failed to fetch minimal training programs');
  }
};

/**
 * Fetch full training programs (complete data)
 */
const fetchFullTrainingPrograms = async (apiBaseUrl, authToken) => {
  const {callAPI} = useAPI();
  
  if (!authToken || !apiBaseUrl) {
    throw new Error('API base URL and auth token are required');
  }

  try{
    const data = await callAPI(
      `trainingprograms?show_subscribed=true`,
      {
        token: authToken ,
      }
    );
    return data
  }catch(error){
    throw new Error('Failed to fetch full training programs');
  }

};

/**
 * Custom hook to fetch training programs with React Query
 * First fetches minimal data for fast initial load, then fetches full data
 * Stale time is set to 10 minutes
 * Automatically refetches when data becomes stale
 */
export function useTrainingProgramQuery(apiBaseUrl, authToken, enabled = true, is2FA = false) {
  const canFetchMinimal = enabled && !!authToken && !!apiBaseUrl && !is2FA;

  const minimal = useQuery({
    queryKey: ['trainingPrograms', 'minimal', apiBaseUrl, authToken],
    queryFn: () => fetchMinimalTrainingPrograms(apiBaseUrl, authToken),
    enabled: canFetchMinimal,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnMount: true, // Refetch on mount if data is stale
    refetchIntervalInBackground: false,
  });

  const full = useQuery({
    queryKey: ['trainingPrograms', 'full', apiBaseUrl, authToken],
    queryFn: () => fetchFullTrainingPrograms(apiBaseUrl, authToken),
    // Dependency: Only starts once minimal.data exists
    enabled: canFetchMinimal && !!minimal.data, 
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnMount: true, // Refetch on mount if data is stale
    refetchIntervalInBackground: false,
  });

  return {
    // Priority: Full Data > Minimal Data > Null
    data: full.data || minimal.data || null,
    // It's loading if the minimal hasn't finished OR if we are waiting for full
    isLoading: minimal.isLoading || (minimal.data && full.isLoading),
    error: minimal.error || full.error,
  };
}